/**
 * Admin Context
 * Manages SuperAdmin state including selected company for impersonation
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useAuth } from '../hooks/useAuth';
import { setImpersonateCompanyId } from '../services/api';

interface AdminContextType {
  selectedCompanyId: string | null;
  selectedCompanyName: string | null;
  selectedTradeName: string | null;
  setSelectedCompany: (companyId: string | null, name?: string | null, tradeName?: string | null) => void;
  /** @deprecated use setSelectedCompany */
  setSelectedCompanyId: (companyId: string | null) => void;
  isImpersonating: boolean;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedCompanyId, setSelectedCompanyIdState] = useState<string | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | null>(null);
  const [selectedTradeName, setSelectedTradeName] = useState<string | null>(null);

  // Reset selected company when user changes or logs out
  useEffect(() => {
    if (!user || user.role !== 'superadmin') {
      setSelectedCompanyIdState(null);
      setSelectedCompanyName(null);
      setSelectedTradeName(null);
      setImpersonateCompanyId(null);
    }
  }, [user]);

  // Full setter with name info
  const setSelectedCompany = (companyId: string | null, name?: string | null, tradeName?: string | null) => {
    setSelectedCompanyIdState(companyId);
    setSelectedCompanyName(name ?? null);
    setSelectedTradeName(tradeName ?? null);
    setImpersonateCompanyId(companyId);
    queryClient.invalidateQueries();
  };

  // Legacy setter (keeps backward compat)
  const setSelectedCompanyId = (companyId: string | null) => {
    setSelectedCompany(companyId);
  };

  const isImpersonating = user?.role === 'superadmin' && selectedCompanyId !== null;

  return (
    <AdminContext.Provider
      value={{
        selectedCompanyId,
        selectedCompanyName,
        selectedTradeName,
        setSelectedCompany,
        setSelectedCompanyId,
        isImpersonating,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};
